import React from "react"

import { DollarSign, Package, TrendingUp, Truck, User, Zap } from "lucide-react"

import { Separator } from "@/components/ui/separator"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"

export interface CostLine {
  kind: "lease" | "charging" | "wage"
  label: string
  detail?: string | null
  /** Null shows a dash. */
  value: number | null
  /** Greys the row out and shows this note in place of a figure. */
  unavailable?: string
}

export type CostCardDensity = "compact" | "regular" | "full"

interface CostBreakdownCardProps {
  title: string
  subtitle: React.ReactNode
  density: CostCardDensity
  lines: CostLine[]
  total: number
  /** Haulage revenue; above zero adds the net cost bar. */
  revenue?: number | null
  revenueLabel?: string
  /** Keep the net cost bar but leave out the revenue row above it. */
  hideRevenueRow?: boolean
  perParcel?: { cost: number | null | undefined; parcels: number | null | undefined }
}

const LINE_ICON = {
  lease: { Icon: Truck, bg: "bg-blue-100", fg: "text-blue-600" },
  charging: { Icon: Zap, bg: "bg-green-100", fg: "text-green-600" },
  wage: { Icon: User, bg: "bg-purple-100", fg: "text-purple-600" },
} as const

const DENSITY = {
  compact: {
    frame: "rounded-lg p-3",
    header: "mb-2",
    headerIconWrap: "p-1",
    headerIcon: 12,
    title: "text-xs",
    subtitle: "text-[10px]",
    lines: "space-y-1.5 mb-2",
    row: "p-1.5",
    gap: "gap-1.5",
    lineIconWrap: "p-0.5",
    lineIcon: 10,
    label: "text-[10px]",
    detail: "text-[10px]",
    value: "text-[10px]",
    separator: null,
    total: "p-1.5 mb-1.5",
    totalIcon: 12,
    totalLabel: "text-[10px]",
    totalValue: "text-sm",
    revenueIcon: 10,
    revenueValue: "text-[10px]",
    net: "p-1.5 mb-1.5",
    netIcon: 12,
    netValue: "text-sm",
    parcelCount: "text-[9px] ml-1",
  },
  regular: {
    frame: "rounded-lg p-4",
    header: "mb-3",
    headerIconWrap: "p-1.5",
    headerIcon: 14,
    title: "text-xs",
    subtitle: "text-[10px]",
    lines: "space-y-2 mb-3",
    row: "p-2",
    gap: "gap-2",
    lineIconWrap: "p-1",
    lineIcon: 12,
    label: "text-xs",
    detail: "text-[10px]",
    value: "text-xs",
    separator: null,
    total: "p-2 mb-2",
    totalIcon: 14,
    totalLabel: "text-xs",
    totalValue: "text-base",
    revenueIcon: 12,
    revenueValue: "text-xs",
    net: "p-2 mb-2",
    netIcon: 12,
    netValue: "text-xs",
    parcelCount: "text-[10px] ml-1",
  },
  full: {
    frame: "rounded-xl p-4 shadow-sm",
    header: "mb-3",
    headerIconWrap: "p-1.5",
    headerIcon: 16,
    title: "text-sm",
    subtitle: "text-xs",
    lines: "space-y-2 mb-3",
    row: "p-2",
    gap: "gap-2",
    lineIconWrap: "p-1",
    lineIcon: 14,
    label: "text-xs",
    detail: "text-[10px]",
    value: "text-sm",
    separator: "my-3",
    total: "p-3 mb-2",
    totalIcon: 16,
    totalLabel: "text-sm",
    totalValue: "text-xl",
    revenueIcon: 14,
    revenueValue: "text-sm",
    net: "p-3 mb-2",
    netIcon: 16,
    netValue: "text-xl",
    parcelCount: "text-[10px] ml-2",
  },
} as const

const CostBreakdownCard: React.FC<CostBreakdownCardProps> = ({
  title,
  subtitle,
  density,
  lines,
  total,
  revenue,
  revenueLabel = "Revenue",
  hideRevenueRow = false,
  perParcel,
}) => {
  const d = DENSITY[density]
  const hasRevenue = revenue != null && revenue > 0
  const parcelCost = perParcel?.cost
  const parcels = perParcel?.parcels
  const showPerParcel = parcelCost != null && parcelCost > 0 && parcels != null && parcels > 0

  return (
    <div
      className={cn(
        "bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50",
        d.frame,
      )}
    >
      <div className={cn("flex items-center gap-2", d.header)}>
        <div className={cn("bg-blue-100 rounded-lg", d.headerIconWrap)}>
          <DollarSign size={d.headerIcon} className="text-blue-700" />
        </div>
        <div>
          <h4 className={cn("font-semibold text-palette-jungleGreen900", d.title)}>{title}</h4>
          <p className={cn("text-palette-jungleGreen500", d.subtitle)}>{subtitle}</p>
        </div>
      </div>

      <div className={d.lines}>
        {lines.map((line) => {
          const { Icon, bg, fg } = LINE_ICON[line.kind]
          if (line.unavailable) {
            return (
              <div
                key={line.kind}
                className={cn(
                  "flex items-center justify-between bg-palette-jungleGreen000 rounded-lg",
                  "border border-palette-jungleGreen200",
                  d.row,
                )}
              >
                <div className={cn("flex items-center", d.gap)}>
                  <div className={cn("bg-palette-jungleGreen100 rounded-md", d.lineIconWrap)}>
                    <Icon size={d.lineIcon} className="text-palette-jungleGreen400" />
                  </div>
                  <div className={cn("text-palette-jungleGreen500", d.label)}>{line.label}</div>
                </div>
                <div className={cn("text-palette-jungleGreen400", d.detail)}>
                  {line.unavailable}
                </div>
              </div>
            )
          }
          return (
            <div
              key={line.kind}
              className={cn(
                "flex items-center justify-between bg-white/60 rounded-lg border border-blue-100",
                d.row,
              )}
            >
              <div className={cn("flex items-center", d.gap)}>
                <div className={cn("rounded-md", bg, d.lineIconWrap)}>
                  <Icon size={d.lineIcon} className={fg} />
                </div>
                <div>
                  <div className={cn("font-medium text-palette-jungleGreen900", d.label)}>
                    {line.label}
                  </div>
                  {line.detail && (
                    <div className={cn("text-palette-jungleGreen500", d.detail)}>
                      {line.detail}
                    </div>
                  )}
                </div>
              </div>
              <div className={cn("font-semibold text-palette-jungleGreen900", d.value)}>
                {line.value == null ? "—" : formatCost(line.value)}
              </div>
            </div>
          )
        })}
      </div>

      {d.separator && <Separator className={d.separator} />}

      <div className={cn("flex items-center justify-between bg-blue-600 rounded-lg", d.total)}>
        <div className={cn("flex items-center", d.gap)}>
          <TrendingUp size={d.totalIcon} className="text-white" />
          <span className={cn("font-medium text-white", d.totalLabel)}>Total Cost</span>
        </div>
        <span className={cn("font-bold text-white", d.totalValue)}>{formatCost(total)}</span>
      </div>

      {hasRevenue && (
        <>
          {!hideRevenueRow && (
            <div
              className={cn(
                "flex items-center justify-between bg-green-50 rounded-lg border border-green-200",
                d.row,
                "mb-2",
              )}
            >
              <div className={cn("flex items-center", d.gap)}>
                <DollarSign size={d.revenueIcon} className="text-green-600" />
                <span className={cn("font-medium text-palette-jungleGreen900", d.label)}>
                  {revenueLabel}
                </span>
              </div>
              <span className={cn("font-semibold text-green-700", d.revenueValue)}>
                {formatCost(revenue)}
              </span>
            </div>
          )}
          <div
            className={cn(
              "flex items-center justify-between bg-palette-jungleGreen600 rounded-lg",
              d.net,
            )}
          >
            <div className={cn("flex items-center", d.gap)}>
              <TrendingUp size={d.netIcon} className="text-white" />
              <span className={cn("font-medium text-white", d.totalLabel)}>Net Cost</span>
            </div>
            <span className={cn("font-bold text-white", d.netValue)}>
              {formatCost(total - revenue)}
            </span>
          </div>
        </>
      )}

      {showPerParcel && (
        <div
          className={cn(
            "flex items-center justify-between bg-white/80 rounded-lg border border-blue-200",
            d.row,
          )}
        >
          <div className={cn("flex items-center", d.gap)}>
            <Package size={d.lineIcon} className="text-blue-600" />
            <div>
              <span className={cn("font-medium text-palette-jungleGreen900", d.label)}>
                Cost per Parcel
              </span>
              <span className={cn("text-palette-jungleGreen500", d.parcelCount)}>
                ({parcels.toLocaleString()} parcels)
              </span>
            </div>
          </div>
          <span className={cn("font-bold text-blue-700", d.value)}>
            {formatCost(parcelCost)}
          </span>
        </div>
      )}
    </div>
  )
}

export default CostBreakdownCard
